import React, { createRef, useContext, useEffect, useState } from 'react';

import Map from 'common/components/Map/Map';
import MapService from 'common/services/Map';
import StateContext from 'common/contexts/state-context';
import BasicButton from 'common/components/Widgets/BasicButton/BasicButton';
import IconsMapper from 'common/components/IconsMapper/IconsMapper';
import { AsYouType } from 'libphonenumber-js';
import Switch from 'common/components/Widgets/Switch/Switch';
import { useMemo } from 'react';
import './MerchantList.css';
import {
  ButtonBasicStyle,
  HAIRNET_ID,
  ROOT_IMG_URL,
} from '../../../GlobalValues';
import { useDevice } from 'common/customHooks';
import Itinary from '../../../widgets/Itinary';

const MerchantList = () => {
  const device = useDevice();
  const mapRef = createRef();
  const isMobileOrTablet = device === 'mobile' || device === 'tablet';
  const desktopMapHeight = {
    desktop: '400px',
    tablet: '300px',
    mobile: '200px',
    hidden: '0px',
  };
  const [state] = useContext(StateContext);
  const [mapSize, setMapSize] = useState(desktopMapHeight[device]);
  const [displayMap, setDisplayMap] = useState(false);

  useEffect(() => {
    if (isMobileOrTablet) {
      setMapSize(
        displayMap ? desktopMapHeight[device] : desktopMapHeight['hidden'],
      );
    } else {
      setMapSize(desktopMapHeight[device]);
    }
  }, [device, displayMap, desktopMapHeight, isMobileOrTablet]);

  const toggleMapDisplay = () => {
    setDisplayMap((prevDisplayMap) => !prevDisplayMap);
  };

  return (
    <section className="merchant-root">
      {isMobileOrTablet ? (
        <div
          className="sl-flex sl-ai-center sl-jc-center sl-gap sl-gutter-m"
          style={{ backgroundColor: 'var(--fag-color)' }}
        >
          <label htmlFor="displayMapLabel" style={{ fontWeight: 'bold' }}>
            Afficher la carte
          </label>
          <Switch
            defaultChecked={displayMap}
            onChange={toggleMapDisplay}
            labelId="displayMapLabel"
            aria-label="Afficher la carte"
            style={{
              width: 40,
              height: 20,
              border: '2px solid var(--secondary-color)',
              padding: 0,
              borderRadius: 16,
              alignItems: 'center',
              display: 'inline-flex',
              cursor: 'pointer',
              position: 'relative',
              transition: 'border-color 0.2s',
            }}
          >
            {({ checked }) => (
              <span
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: 'var(--secondary-color)',
                  left: checked ? 20 : 2,
                  position: 'absolute',
                  display: 'block',
                  transition: 'left 0.2s',
                }}
              />
            )}
          </Switch>
        </div>
      ) : null}

      <div ref={mapRef}>
        <Map
          mapSize={{
            height: mapSize,
            width: '100%',
          }}
          mapConfig={{
            scrollwheel: false,
            styles: MapService.mapStyles.silver,
          }}
        />
      </div>

      <article>
        {state.merchants && (
          <div className="merchant-items sl-flex sl-flex-col">
            <ul className="sl-container">
              {state.merchants
                .sort((a, b) => {
                  return a.distance - b.distance;
                })
                .map((merchant, index) => {
                  return (
                    <React.Fragment key={merchant.id}>
                      <Merchant merchant={merchant} index={index} />
                      <li className="merchant-divider" aria-hidden="true">
                        <hr />
                      </li>
                    </React.Fragment>
                  );
                })}
            </ul>
          </div>
        )}
      </article>
    </section>
  );
};

export default MerchantList;

const Merchant = ({ merchant }) => {
  return (
    <li className="sl-flex row-to-col-mob sl-ai-stretch merchant-item">
      <MerchantPreview merchant={merchant} />
      <MerchantAddress merchant={merchant} />
    </li>
  );
};

const MerchantCtas = ({ merchant }) => {
  const phoneNumber = merchant?.phone
    ? new AsYouType().input(merchant.phone)
    : null;
  return (
    <div className="sl-flex col-to-row-mob sl-gap sl-jc-center">
      {phoneNumber && (
        <BasicButton
          label={phoneNumber}
          isBig={true}
          href={'tel:' + merchant.phone}
          icon={'faPhone'}
          hoverColor={'var(--tertiary-color)'}
          hover={'var(--secondary-color)'}
          style={{
            ...ButtonBasicStyle,
            backgroundColor: 'var(--primary-color)',
          }}
        />
      )}
      {merchant.extended_data.extended_string_data_3 && (
        <BasicButton
          label={'PRENDRE RDV'}
          icon={'faCalendarDays'}
          isBig={true}
          href={`${merchant.localpageUrl}/#${HAIRNET_ID}`}
          hover={'var(--secondary-color)'}
          style={{
            ...ButtonBasicStyle,
            backgroundColor: 'var(--tertiary-color)',
          }}
        />
      )}
    </div>
  );
};

const MerchantAddress = ({ merchant }) => {
  return (
    <div className="sl-flex sl-flex-col sl-gap sl-grow-1">
      <div className="sl-title-bloc sl-title merchant-name">
        <a href={merchant.localpageUrl}>{merchant.name}</a>
      </div>
      <div className="sl-flex sl-gap sl-grow-1 sl-relative">
        <div className="sl-flex sl-ai-center sl-gap-l sl-grow-1 row-to-col-mob">
          <div className="sl-flex sl-flex-col sl-gap sl-jc-center sl-grow-1 sl-ai-center-mob">
            <address className="sl-flex sl-flex-col address">
              <strong>{merchant.address.street}</strong>
              <strong>
                {merchant.address.zip} {merchant.address.city}
              </strong>
            </address>
            <Itinary merchant={merchant} />
          </div>
          <MerchantCtas merchant={merchant} />
        </div>
        <a
          className="next sl-flex sl-ai-center sl-jc-center sl-cursor-pointer"
          href={merchant.localpageUrl}
        >
          <IconsMapper
            icon={'faAngleRight'}
            style={{ fontSize: 'clamp(1.5rem, 2.5vw, 4rem)' }}
          />
        </a>
      </div>
    </div>
  );
};

const MerchantPreview = ({ merchant }) => {
  const fallbackPreview = useMemo(() => {
    const fallbackPreviews = [
      'preview-fb-1.png',
      'preview-fb-2.png',
      'preview-fb-3.png',
      'preview-fb-4.png',
    ];
    const randomIndex = Math.floor(Math.random() * fallbackPreviews.length);
    return fallbackPreviews[randomIndex];
  }, []);

  return (
    <a
      className="sl-relative preview-container sl-flex sl-ai-center"
      href={merchant.localpageUrl}
    >
      <div
        className="sl-absolute"
        style={{
          width: '100%',
          height: '100%',
          top: '0',
          left: '0',
          display: 'flex',
          alignItems: 'center',
          zIndex: '3',
        }}
      >
        <div
          style={{
            width: 'fit-content',
            padding: 'var(--gutter-m)',
            borderRadius: 'var(--b-radius)',
            backgroundColor: 'var(--secondary-color)',
            opacity: '0.8',
            alignItems: 'center',
            fontWeight: 'bold',
            color: 'var(--tertiary-color)',
          }}
        >
          {merchant.distance.toPrecision(3)} Km
        </div>
      </div>
      <img
        src={`${ROOT_IMG_URL}/${fallbackPreview}`}
        alt="hairdresser local store"
        className="preview-radius"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: '2',
        }}
        loading="lazy"
      />
      <div
        style={{ overflow: 'hidden', width: 'var(--box-height)' }}
        className="desktop-only"
      >
        <img
          src={`${ROOT_IMG_URL}/halfmandala.png`}
          alt="mandala decoration"
          style={{
            height: 'var(--box-height)',
            zIndex: '1',
          }}
        />
      </div>
    </a>
  );
};
